import { Address } from "../types/address";
import { getLocale, t } from "../i18n";
import { countiesValuesCompare } from "../components/profile/AdditionalDataForm";

export const formatAddress = (a: Address, hiddenCountry?: boolean): string => {
  const country = hiddenCountry ? "" : `, ${t(`countries.${a.addressCountryCode}`)}`;
  const houseNumber = a.houseNumber ? `${a.houseNumber}${a.apartmentNumber ? `-${a.apartmentNumber}` : ''}` : '';
  const county = a.county ? `, ${countiesValuesCompare(a.county)}` : '';
  const city = a.city ? `, ${a.city}` : '';

  const addressParts = [
    a.postalCode,
    a.street,
    houseNumber,
    city,
    county,
    country
  ].filter(part => part !== ''); // Remove empty parts

  // Join the non-empty parts to form the address
  let formattedAddress = addressParts.join(' ').trim();

  // Replace any sequence of spaces with a single space
  formattedAddress = formattedAddress.replace(/\s+/g, ' ');

  // Replace ", ," with ", "
  formattedAddress = formattedAddress.replace(/,\s*,/g, ', ')

   // Replace " , " with ", "
  formattedAddress = formattedAddress.replace(/\s*,\s*/g, ', ');

  return formattedAddress;
}

export const formatCurrency = (amount: number, digits = 0): string => {
  let displayAmount = amount;
  if (amount < 0 && -1 * 10 ** -digits < parseFloat(amount.toFixed(digits))) displayAmount = -amount;
  return displayAmount.toLocaleString(
    getLocale(),
    { style: "currency", currency: App.currency.code, maximumFractionDigits: 2, minimumFractionDigits: digits },
  );
}

export const formatMonthsToYear = (months: number): string => t("years", { count: months / 12 });

export const formatYearsWithMonths = (months: number): string => {
  const yearsCount = Math.floor(months / 12)
  const monthsCount = months % 12

  if ( yearsCount === 0) {
    return t("months", { count: monthsCount })
  } else if (monthsCount === 0) {
    return t("years", { count: yearsCount})
  } else {
    return(`
      ${yearsCount} ${t("short_years", { count: yearsCount })}
      ${monthsCount} ${t("short_months", { count: monthsCount })}
    `)
  }
}

export const formatMonthsUpToYear = (months: number) =>
  months === 12 ? t("investments.up_to_1_year") : t("investments.up_to_n_years", { years: months / 12 });

export const formatNumber = (n: number, digits = 0): string => n.toLocaleString(
  getLocale(),
  { maximumFractionDigits: 2, minimumFractionDigits: digits },
);

export const formatPercent = (n: number, digits: number = 2): string => (
  n.toLocaleString(getLocale(), { maximumFractionDigits: digits, minimumFractionDigits: digits }) + "%"
)

export const formatDecimalToPercent = (n: number, digits: number = 2): string => {
  return formatPercent(n * 100, digits)
}

export const formatPhoneNumber = (p: { phoneCode?: string, phoneNumber?: string }): string => (
  p.phoneCode && p.phoneNumber ? `${p.phoneCode} ${p.phoneNumber}` : ""
)

export const formatUserName = (u: BaseUserInfo): string => (
  u.firstName || u.lastName ? `${u.firstName}\u00A0${u.lastName}` : u.email
)

const dateFormatter = new Intl.DateTimeFormat("et")
const timeFormatter = new Intl.DateTimeFormat("et", { hour: "numeric", minute: "numeric" });

export const formatDate = (dateOrStr: Date | string  = new Date()): string => {
  const date = typeof(dateOrStr) === "string" ? new Date(dateOrStr) : dateOrStr;
  return dateFormatter.format(date);
}

export const formatTime = (time: Date) => timeFormatter.format(time);

export const formatDateAndTime = (dateOrStr: Date | string = new Date()): string => {
  const date = typeof (dateOrStr) === "string" ? new Date(dateOrStr) : dateOrStr;
  return `${dateFormatter.format(date)} ${t("at")} ${timeFormatter.format(date)}`;
}

export const formatDuration = (seconds: number): string => {
  return seconds > 0
    ? Intl.DateTimeFormat("et", { minute: "numeric", second: "numeric" }).format(seconds * 1000)
    : ""
}
